import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideSearchApi, SEARCH_API } from '../../../api/services/search.service';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, from, map, mergeMap, of, takeUntil } from 'rxjs';
import { TranslocoPipe } from '@jsverse/transloco';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatHint } from '@angular/material/form-field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { DatePipe } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { mapDateOrNull } from '../../../shared/helpers/date.func';
import { BaseDestroy } from '../../../core/components/base-destroy/base-destroy';
import { Router } from '@angular/router';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { PERSON_TYPE } from '../../../api/app-types/search.app';
import { SponsorPersonSuggestion } from '../../../api/app-types/sponsor.app';

@Component({
	selector: 'lvr-new-sponsor-person-dialog',
	imports: [
		TranslocoPipe,
		MatDialogModule,
		MatIcon,
		MatFormField,
		LvrInputDirective,
		LvrPaddingDirective,
		ReactiveFormsModule,
		MatAutocompleteModule,
		MatHint,
		MatInput,
		DatePipe,
		LvrButton,
	],
	providers: [provideSearchApi],
	templateUrl: './new-sponsor-person-dialog.html',
	styles: `
		.dialog-window {
			display: flex;
			flex-direction: column;
			min-height: 300px;
			justify-content: space-between;
		}
	`,
})
export class NewSponsorPersonDialog extends BaseDestroy implements OnInit {
	private readonly router = inject(Router);
	private readonly fb = inject(FormBuilder);
	private readonly api = inject(SEARCH_API);
	public readonly selfRef = inject(MatDialogRef<NewSponsorPersonDialog>);
	private readonly data = inject(MAT_DIALOG_DATA);
	private readonly sponsorId = this.data.sponsorId;
	public readonly form = this.initForm();
	public readonly suggestions = signal<SponsorPersonSuggestion[]>([]);
	private readonly sponsorPerson = toSignal<SponsorPersonSuggestion | string | null>(
		this.form.get('person')!.valueChanges.pipe(debounceTime(300)),
		{ initialValue: null },
	);
	public readonly showWarning = computed(
		() => this.suggestions().length > 0 && typeof this.sponsorPerson() === 'string',
	);
	public readonly btnText = computed(() =>
		typeof this.sponsorPerson() === 'string'
			? 'sponsor.person_create.dialog.create'
			: 'sponsor.person_create.dialog.select',
	);

	public ngOnInit() {
		this.registerFetchSuggestions();
	}

	public displayName(person?: SponsorPersonSuggestion) {
		if (!person) return '';
		return `${person.firstName ?? ''} ${person.lastName}`.trim();
	}

	public done() {
		const personValue = this.form.get('person')!.value;

		if (personValue && typeof personValue === 'object') {
			const sponsorId = personValue.sponsor?.id ?? this.sponsorId;
			this.router.navigate(['/desk/sponsor', sponsorId, 'persons', personValue.id]);
			this.selfRef.close();
			return;
		}

		this.router.navigate(['/desk/sponsor', this.sponsorId, 'persons', 'add']);
		this.selfRef.close();
	}

	private initForm() {
		return this.fb.group({
			person: this.fb.control<SponsorPersonSuggestion | string | null>(null, { validators: [Validators.required] }),
		});
	}

	private registerFetchSuggestions() {
		this.form
			.get('person')!
			.valueChanges.pipe(
				takeUntil(this.destroy$),
				debounceTime(300),
				map((value) => (typeof value === 'string' ? value.trim() : '')),
				distinctUntilChanged(),
				mergeMap((searchTerm) => {
					if (!searchTerm) return of(null);
					return from(
						this.api.search({
							searchInstitutions: false,
							searchInstitutionBranches: false,
							searchClientEnvironments: false,
							searchClients: false,
							searchUsers: false,
							searchInstitutionPerson: false,
							searchSponsors: false,
							searchSponsorPerson: true,
							searchIso: false,
							searchTerm,
						}),
					);
				}),
				map((result) => {
					if (!result) return [];
					return result.entities
						.filter((x) => x.personType === PERSON_TYPE.SPONSOR_CONTACT_PERSON)
						.map((item): SponsorPersonSuggestion => {
							const sponsor = (item.item as { sponsor?: { id?: number; name?: string; integrationOfficeId?: number } })
								.sponsor;
							return {
								id: item.item.id,
								firstName: item.item.firstName,
								lastName: item.item.name,
								dateOfBirth: mapDateOrNull(item.item.dateOfBirth)?.toString(),
								sponsor: sponsor
									? {
											id: sponsor.id ?? this.sponsorId,
											name: sponsor.name ?? '',
											integrationOfficeId: sponsor.integrationOfficeId ?? 0,
										}
									: undefined,
							};
						});
				}),
			)
			.subscribe((persons) => this.suggestions.set(persons));
	}
}
